import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Table, Column } from "@tanstack/react-table";
import { X, RotateCcw } from "lucide-react";
import SortableColumnRow from "./SortableColumnRow";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

interface ColumnConfigProps<T> {
  table: Table<T>;
  onClose: () => void;
  targetColumnId?: string | null;
}

const ColumnConfig = <T,>({
  table,
  onClose,
  targetColumnId = null,
}: ColumnConfigProps<T>) => {
  console.log(targetColumnId)
  if (targetColumnId) console.log(table.getColumn(targetColumnId)?.columnDef.header)
  const initialSearch = useMemo(() => {
    if (!targetColumnId) return "";
    const col = table.getColumn(targetColumnId);
    return (col?.columnDef.header as string) || "";
  }, [table, targetColumnId]);

  const [expandedColumnId, setExpandedColumnId] = useState<string | null>(targetColumnId);
  const [columnSearch, setColumnSearch] = useState(initialSearch);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // 1. Get ALL columns directly.
  // We'll filter out columns that don't have a header (like selection)
  // or columns that are explicitly marked as internal.
  const allColumns = useMemo(() => {
    return table.getAllColumns().filter((col) => {
      // Only show columns that have a header and aren't the 'select' column
      return col.id !== "select" && col.columnDef.header;
    });
  }, [table]);

  // 2. Sync the order IDs
  const { columnOrder } = table.getState();

  const currentOrderIds = useMemo(() => {
    const allIds = allColumns.map((c) => c.id);
    if (!columnOrder || columnOrder.length === 0) return allIds;

    // Ensure we only include IDs that actually exist in our current columns
    return columnOrder.filter((id) => allIds.includes(id));
  }, [columnOrder, allColumns]);

  // 3. Sort the column objects based on the ID order
  const sortedColumns = useMemo(() => {
    return [...allColumns].sort(
      (a, b) => currentOrderIds.indexOf(a.id) - currentOrderIds.indexOf(b.id),
    );
  }, [allColumns, currentOrderIds]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = currentOrderIds.indexOf(active.id as string);
      const newIndex = currentOrderIds.indexOf(over.id as string);

      const newOrder = arrayMove(currentOrderIds, oldIndex, newIndex);
      // We keep 'select' at the front if it exists
      table.setColumnOrder(["select", ...newOrder]);
    }
  };

  const handleReset = () => {
    table.resetColumnVisibility();
    table.resetSorting();
    table.setColumnFilters([]);
    table.setColumnOrder(["select", ...allColumns.map((c) => c.id)]);
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-3xl shadow-2xl flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white">
            Configure View
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        <div className="px-6 pt-4">
          <input
            type="text"
            placeholder="Search columns..."
            value={columnSearch}
            onChange={(e) => setColumnSearch(e.target.value)}
            className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
          />
        </div>

        <div className="p-6 space-y-2 overflow-y-auto flex-1 custom-scrollbar bg-white dark:bg-slate-900">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            {/* The items prop MUST be a clean string[] */}
            <SortableContext
              items={currentOrderIds}
              strategy={verticalListSortingStrategy}
            >
              {sortedColumns
                .filter((col) => {
                  const label = (col.columnDef.header as string) || "";
                  return label
                    .toLowerCase()
                    .includes(columnSearch.toLowerCase());
                })
                .map((column) => (
                  <SortableColumnRow
                    key={column.id}
                    column={column}
                    isExpanded={expandedColumnId === column.id}
                    onExpand={() =>
                      setExpandedColumnId(
                        expandedColumnId === column.id ? null : column.id,
                      )
                    }
                  />
                ))}
            </SortableContext>
          </DndContext>
        </div>

        <div className="p-6 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-between border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-rose-500 transition-colors uppercase tracking-widest"
          >
            <RotateCcw size={14} />
            Reset Defaults
          </button>
          <button
            onClick={onClose}
            className="px-8 py-3 bg-primary hover:bg-primary/90 rounded-2xl font-bold text-white transition-all shadow-lg shadow-primary/20 active:scale-95 text-sm"
          >
            Apply View
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ColumnConfig;
